import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '../lib/api';

/**
 * PAPER -> LIVE PROMOTION LADDER (God 2026-09-30).
 *
 * "Paper trading me pass ho ke live trading me approval mile, approve ho ke KALI live trading me auto deploy ho.
 * Approve list me aane ke liye rule: win rate 60%+, kam se kam 20 trading" - and net all-inclusive > 0 (God's
 * choice the same hour). Built ON the Phase 2 D6 gateway: the list is /kali/gateway/candidates, Approve is
 * step 1 (/kali/gateway/verify - the server re-checks every rule in the packet, so this page cannot let a cell
 * through that does not qualify), and after the gateway's own 10-minute wait ONE click Confirm is step 2
 * (/kali/gateway/confirm, typed cell + sha8 filled in for God - his choice), which rebuilds the packet,
 * compares its hash and writes the KALI LIVE leg. Master session only.
 */

type Rules = { trades_ok: boolean; sessions_ok: boolean; win_ok: boolean; net_ok: boolean };
type Row = {
  cell: string; bot: string; symbol: string; tf: string; stage: 'PAPER' | 'PASS' | 'APPROVED' | 'LIVE';
  trades: number; sessions: number; win_rate: number | null; net: number | null; max_drawdown: number | null;
  rules: Rules; why: string; suggested_max_qty: number;
  gateway: { state?: string; verified_at?: number; confirm_after?: number; expires_at?: number; max_qty?: number;
             pine_sha8?: string | null };
};
type Data = { ts: string; rules: { min_win: number; min_trades: number; min_sessions: number; sessions_window: number;
  basis: string; confirm_wait_s: number }; counts: Record<string, number>; cells: Row[] };
type Item = { item: string; status: string; detail: string };

const BOT: Record<string, string> = {
  mai_veg: 'Vega', mai_veg_mcx: 'Vega Max', mai_shool: 'Shool', mai_shool_mcx: 'Shool Max', mai_sthambh: 'Sthambh', mai_sthambh_etf: 'Sthambh ETF',
  mai_veg_sail: 'Vega Sell', mai_veg_mcx_sail: 'Vega Max Sell',
};
const STAGE = {
  PAPER: { t: '📝 PAPER — evidence ban raha', c: '#94a3b8' },
  PASS: { t: '✅ PASS — approval list', c: '#10b981' },
  APPROVED: { t: '⏳ APPROVED — 10 min wait', c: '#f59e0b' },
  LIVE: { t: '🔴 KALI LIVE', c: '#ef4444' },
} as const;
const rs = (v: number | null) => v == null ? '—' : `${v < 0 ? '−' : '+'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;

const Bar: React.FC<{ v: number; goal: number; label: string; ok: boolean }> = ({ v, goal, label, ok }) => (
  <div style={{ fontSize: 10, color: ok ? '#10b981' : '#94a3b8' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{label}</span><span>{ok ? '✓' : '✗'}</span></div>
    <div style={{ height: 4, background: '#1e293b', borderRadius: 2 }}>
      <div style={{ width: `${Math.max(0, Math.min(100, goal ? 100 * v / goal : 0))}%`, height: 4, borderRadius: 2,
                    background: ok ? '#10b981' : '#f59e0b' }} />
    </div>
  </div>
);

const PromotionLadder: React.FC = () => {
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [pkt, setPkt] = useState<{ cell: string; items: Item[]; ok: boolean; maxq: number } | null>(null);
  const [qty, setQty] = useState<Record<string, number>>({});
  const [now, setNow] = useState(Date.now() / 1000);

  const load = async (fresh = false) => {
    const r = await apiGet<Data>(`/kali/gateway/candidates?fresh=${fresh ? 1 : 0}&t=${Date.now()}`, { timeoutMs: 30000 });
    if (r.ok && r.data) { setData(r.data); setErr(''); }
    else setErr(r.status === 401 || r.status === 403 ? 'Master Admin login chahiye (gateway sirf signed-in Master ke liye)'
                : r.status === 404 ? 'promotion API abhi deploy nahi hua (404)' : (r.error || 'load failed'));
  };
  useEffect(() => {
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 30000);
    const tk = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => { clearInterval(iv); clearInterval(tk); };
  }, []);

  const by = useMemo(() => {
    const m: Record<string, Row[]> = { PAPER: [], PASS: [], APPROVED: [], LIVE: [] };
    for (const r of data?.cells || []) m[r.stage].push(r);
    return m;
  }, [data]);

  const openPacket = async (r: Row) => {
    const mq = qty[r.cell] || r.suggested_max_qty;
    setBusy(r.cell); setMsg('');
    const res = await apiGet<any>(`/kali/gateway/packet?bot=${r.bot}&symbol=${r.symbol}&tf=${r.tf}&max_qty=${mq}`, { timeoutMs: 60000 });
    setBusy('');
    if (res.ok && res.data) setPkt({ cell: r.cell, items: res.data.items || [], ok: !!res.data.ok, maxq: mq });
    else setMsg(`Packet nahi bana: ${res.error}`);
  };
  const approve = async (r: Row) => {
    if (!pkt || pkt.cell !== r.cell) return;
    setBusy(r.cell);
    const res = await apiPost<any>('/kali/gateway/verify', { bot: r.bot, symbol: r.symbol, tf: r.tf, max_qty: pkt.maxq,
      ticks: pkt.items.map(i => i.item) }, { timeoutMs: 60000 });
    setBusy('');
    setMsg(res.ok ? `✅ ${r.cell} APPROVED (step 1). 10 min baad Confirm khulega.` : `❌ Approve nahi hua: ${res.error}`);
    setPkt(null); load(true);
  };
  const confirm = async (r: Row) => {
    const g = r.gateway;
    if (!g.pine_sha8 || !g.max_qty) return;
    setBusy(r.cell);
    const res = await apiPost<any>('/kali/gateway/confirm', { bot: r.bot, symbol: r.symbol, tf: r.tf, max_qty: g.max_qty,
      typed_cell: r.cell, typed_sha8: g.pine_sha8 }, { timeoutMs: 60000 });
    setBusy('');
    setMsg(res.ok ? `🔴 ${r.cell} KALI LIVE par deploy ho gaya (max_qty ${g.max_qty}).` : `❌ Confirm nahi hua: ${res.error}`);
    load(true);
  };

  const card = (r: Row) => {
    const g = r.gateway;
    const wait = g.confirm_after ? Math.max(0, Math.ceil(g.confirm_after - now)) : 0;
    return (
      <div key={r.cell} style={{ border: '1px solid #1e293b', borderRadius: 8, padding: 8, marginBottom: 6, background: '#0b1220' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
          <b style={{ color: '#e2e8f0', fontSize: 12 }}>{r.symbol} {r.tf}</b>
          <span style={{ color: '#64748b', fontSize: 10 }}>{BOT[r.bot] || r.bot}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, fontSize: 11, margin: '4px 0' }}>
          <span style={{ color: (r.win_rate || 0) >= (data?.rules.min_win || 60) ? '#10b981' : '#cbd5e1' }}>win {r.win_rate ?? '—'}%</span>
          <span style={{ color: '#94a3b8' }}>{r.trades} tr / {r.sessions} din</span>
          <span style={{ color: (r.net || 0) > 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>{rs(r.net)}</span>
        </div>
        {r.stage === 'PAPER' && data && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            <Bar label={`win ${data.rules.min_win}%`} v={r.win_rate || 0} goal={data.rules.min_win} ok={r.rules.win_ok} />
            <Bar label={`trades ${data.rules.min_trades}`} v={r.trades} goal={data.rules.min_trades} ok={r.rules.trades_ok} />
            <Bar label={`din ${data.rules.min_sessions}`} v={r.sessions} goal={data.rules.min_sessions} ok={r.rules.sessions_ok} />
            <Bar label="net > 0" v={(r.net || 0) > 0 ? 1 : 0} goal={1} ok={r.rules.net_ok} />
          </div>
        )}
        {r.stage === 'PASS' && (
          <div style={{ marginTop: 4 }}>
            <label style={{ fontSize: 10, color: '#94a3b8' }}>max_qty{' '}
              <input type="number" min={1} value={qty[r.cell] || r.suggested_max_qty}
                onChange={e => setQty({ ...qty, [r.cell]: Math.max(1, Number(e.target.value) || 1) })}
                style={{ width: 70, background: '#0f172a', color: '#e2e8f0', border: '1px solid #1e293b', borderRadius: 4, fontSize: 11 }} />
            </label>{' '}
            <button disabled={busy === r.cell} onClick={() => openPacket(r)}
              style={{ fontSize: 10.5, fontWeight: 800, padding: '3px 8px', borderRadius: 5, cursor: 'pointer',
                       background: 'rgba(16,185,129,.15)', color: '#10b981', border: '1px solid #10b98155' }}>
              {busy === r.cell ? '…' : 'Packet dekho'}
            </button>
            {pkt && pkt.cell === r.cell && (
              <div style={{ marginTop: 6, fontSize: 10.5 }}>
                {pkt.items.map(i => (
                  <div key={i.item} style={{ color: i.status === 'FAIL' ? '#ef4444' : '#cbd5e1' }}>
                    {i.status === 'FAIL' ? '✗' : '✓'} <b>{i.item}</b> — {i.detail}
                  </div>
                ))}
                <button disabled={!pkt.ok || busy === r.cell} onClick={() => approve(r)}
                  style={{ marginTop: 6, fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 5,
                           cursor: pkt.ok ? 'pointer' : 'not-allowed', background: pkt.ok ? '#10b981' : '#334155',
                           color: '#0f172a', border: 'none' }}>
                  {pkt.ok ? `✅ APPROVE (step 1, max_qty ${pkt.maxq})` : 'Packet me FAIL hai — approve band'}
                </button>
              </div>
            )}
          </div>
        )}
        {r.stage === 'APPROVED' && (
          <div style={{ marginTop: 4, fontSize: 11 }}>
            <div style={{ color: '#f59e0b' }}>max_qty {g.max_qty} · sha {g.pine_sha8}</div>
            <button disabled={wait > 0 || busy === r.cell} onClick={() => confirm(r)}
              style={{ marginTop: 4, fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 5, border: 'none',
                       cursor: wait > 0 ? 'not-allowed' : 'pointer', background: wait > 0 ? '#334155' : '#ef4444', color: '#fff' }}>
              {wait > 0 ? `⏳ Confirm ${Math.floor(wait / 60)}:${String(wait % 60).padStart(2, '0')} me khulega`
                        : '🔴 CONFIRM → KALI LIVE deploy'}
            </button>
          </div>
        )}
        {r.stage === 'LIVE' && <div style={{ fontSize: 10.5, color: '#ef4444', marginTop: 4 }}>LIVE · trading_guard ke 6 gate har order par</div>}
      </div>
    );
  };

  return (
    <div style={{ margin: '0 0 14px', border: '1px solid #1e293b', borderRadius: 10, background: '#0b1220', padding: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline', marginBottom: 8 }}>
        <span style={{ fontWeight: 800, fontSize: 12, letterSpacing: '.06em', color: '#e2e8f0' }}>🚀 PAPER → LIVE PROMOTION</span>
        {data && <span style={{ fontSize: 10.5, color: '#94a3b8' }}>
          niyam: win ≥ {data.rules.min_win}% · ≥ {data.rules.min_trades} trade · ≥ {data.rules.min_sessions} din ·
          net (all-incl) &gt; 0 · pichhle {data.rules.sessions_window} poore paper din (aaj nahi) · {data.ts.slice(11, 16)} IST</span>}
        {err && <span style={{ fontSize: 11, color: '#f87171' }}>⚠ {err}</span>}
      </div>
      {msg && <div style={{ fontSize: 11.5, color: '#e2e8f0', margin: '0 0 8px' }}>{msg}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10 }}>
        {(['PAPER', 'PASS', 'APPROVED', 'LIVE'] as const).map(s => (
          <div key={s}>
            <div style={{ fontSize: 11, fontWeight: 800, color: STAGE[s].c, marginBottom: 6 }}>
              {STAGE[s].t} ({data?.counts[s] || 0})
            </div>
            {(by[s] || []).slice(0, s === 'PAPER' ? 10 : 50).map(card)}
            {s === 'PASS' && data && !by.PASS.length &&
              <div style={{ fontSize: 10.5, color: '#64748b' }}>Abhi koi cell niyam paar nahi karti. Sabse kareeb wali PAPER column me upar hain.</div>}
            {s === 'PAPER' && (by.PAPER || []).length > 10 &&
              <div style={{ fontSize: 10, color: '#475569' }}>+{by.PAPER.length - 10} aur (win-rate ke kram me)</div>}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 10, color: '#475569' }}>
        Approve = gateway step 1 (server har niyam dobara jaanchta hai). 10 min baad Confirm = step 2: packet dobara
        banta hai, hash same ho tabhi KALI LIVE leg likha jata hai. Demote/Disarm KALI page ke Security panel se.
      </div>
    </div>
  );
};

export default PromotionLadder;
